import React, { useMemo } from 'react';
import { View } from 'react-native'
import { useQuery } from '@tanstack/react-query';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import Toggle from '../../../../components/Toggle';
import useActiveAdminCommunity from '../../../../hooks/useActiveAdminCommunity';
import api from '../../../../utils/api';


export interface StaffRole {
  name: string;
  display_name: string;
  is_custom: boolean;
  permissions: { name: string; display_name: string }[];
}


export function useStaffRoles() {
  const { communityId } = useActiveAdminCommunity()

  return useQuery<StaffRole[]>({
    queryKey: ['community.staff.roles', communityId],
    queryFn: () => api.get(`/communities/${communityId}/staff/roles`).then(r => r.data),
  })
}


/**
 * Selector de roles del equipo (uno o varios). Debajo muestra lo que puede hacer
 * la persona: la unión de los permisos de los roles elegidos.
 */
export default function StaffRolePicker({ value, onChange, disabled = false }: {
  value: string[];
  onChange: (roles: string[]) => void;
  disabled?: boolean;
}) {
  const { data: roles, isLoading } = useStaffRoles()

  const toggle = (name: string) => {
    onChange(value.includes(name) ? value.filter(r => r !== name) : [...value, name])
  }

  const permissions = useMemo(() => {
    const byName = new Map<string, string>()
    roles?.filter(role => value.includes(role.name))
      .forEach(role => role.permissions.forEach(p => byName.set(p.name, p.display_name)))
    return [...byName.values()]
  }, [roles, value])

  if (isLoading) {
    return <Text style={tw`text-gray-400 py-4`}>Cargando roles...</Text>
  }

  return (
    <View>
      {roles?.map(role => (
        <View key={role.name} style={tw`row items-center justify-between py-3 border-b border-gray-100`}>
          <View style={tw`flex-1 mr-3`}>
            <Text style={tw`text-secondary`}>{role.display_name}</Text>
            {role.is_custom && <Text style={tw`text-gray-400 text-xs`}>Rol de esta comunidad</Text>}
          </View>
          <Toggle
            value={value.includes(role.name)}
            onValueChange={() => toggle(role.name)}
            disabled={disabled}
          />
        </View>
      ))}

      {permissions.length > 0 && (
        <View style={tw`mt-4`}>
          <Text style={tw`text-gray-500 mb-2`}>Puede:</Text>
          {permissions.map(permission => (
            <Text key={permission} style={tw`text-gray-600 text-sm mb-1`}>· {permission}</Text>
          ))}
        </View>
      )}
    </View>
  )
}
